import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHttpClient } from '../../../shared/hooks/http-hook';
import { PasscodeState, PasscodeFieldConfig, UsePasscodeChangeReturn } from './types';
import { usePasswordVisibility } from './usePasswordVisibility';
import { useScreenReaderAnnouncement } from './useScreenReaderAnnouncement';

const PASSCODE_VALIDATOR = /^\d{4}$/;
const GREEN_CHECK_DISPLAY_DURATION = 2000;

export const usePasscodeChange = (): UsePasscodeChangeReturn => {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [passcodeState, setPasscodeState] = useState<PasscodeState>({
    currentPasscode: '',
    newPasscode: '',
    confirmNewPasscode: '',
    validPasscode: false,
    passcodesMatch: false,
    changeFailed: false,
    currentPasscodeRequired: false,
    newPasscodeRequired: false,
    confirmNewPasscodeRequired: false,
  });

  const [showGreenCheck, setShowGreenCheck] = useState(false);
  const [isAfterSubmit, setIsAfterSubmit] = useState(false);
  const [errorSummary, setErrorSummary] = useState<string[]>([]);

  const [showCurrentPasscode, toggleShowCurrentPasscode, announcementCurrentPasscode] =
    usePasswordVisibility();
  const [showNewPasscode, toggleShowNewPasscode, announcementNewPasscode] = usePasswordVisibility();
  const [showConfirmPasscode, toggleShowConfirmPasscode, announcementConfirmPasscode] =
    usePasswordVisibility();

  const { announcement, announceError, clearAnnouncement } = useScreenReaderAnnouncement();

  const validatePasscode = (passcode: string): boolean => {
    return PASSCODE_VALIDATOR.test(passcode);
  };

  const validatePasscodesMatch = (passcode: string, confirmPasscode: string): boolean => {
    return passcode === confirmPasscode;
  };

  const handlePasscodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPasscodeState((prevState) => {
      const updatedState = {
        ...prevState,
        [name]: value,
      };

      // Reset required validation flags when user starts typing
      if (name === 'currentPasscode' && value.trim()) {
        updatedState.currentPasscodeRequired = false;
        if (prevState.changeFailed) {
          updatedState.changeFailed = false;
        }
      } else if (name === 'newPasscode' && value.trim()) {
        updatedState.newPasscodeRequired = false;
        updatedState.validPasscode = validatePasscode(value);
        updatedState.passcodesMatch = validatePasscodesMatch(value, prevState.confirmNewPasscode);
      } else if (name === 'confirmNewPasscode' && value.trim()) {
        updatedState.confirmNewPasscodeRequired = false;
        updatedState.passcodesMatch = validatePasscodesMatch(value, prevState.newPasscode);
      }

      return updatedState;
    });
  }, []);

  const handleChangePasscode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorSummary([]);

    // Reset isAfterSubmit to ensure focus works on subsequent submissions
    setIsAfterSubmit(false);

    // Collect all validation errors first
    const errors = [];
    const newState = { ...passcodeState };

    // Check required fields
    if (!passcodeState.currentPasscode.trim()) {
      errors.push('currentPasscode');
      newState.currentPasscodeRequired = true;
    } else {
      newState.currentPasscodeRequired = false;
    }

    if (!passcodeState.newPasscode.trim()) {
      errors.push('newPasscode');
      newState.newPasscodeRequired = true;
    } else {
      newState.newPasscodeRequired = false;
    }

    if (!passcodeState.confirmNewPasscode.trim()) {
      errors.push('confirmNewPasscode');
      newState.confirmNewPasscodeRequired = true;
    } else {
      newState.confirmNewPasscodeRequired = false;
    }

    // If current passcode is provided, validate it on backend
    if (passcodeState.currentPasscode.trim()) {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/profile/credentials`,
          'POST',
          JSON.stringify({
            currentCode: passcodeState.currentPasscode,
            newCode: passcodeState.newPasscode || '', // Send empty string if not provided
            type: 'passcode',
          })
        );

        if (responseData) {
          setShowGreenCheck(true);
          setTimeout(() => {
            setShowGreenCheck(false);
          }, GREEN_CHECK_DISPLAY_DURATION);
        }
      } catch (err: any) {
        if (err.status === 403) {
          newState.changeFailed = true;
        } else {
          newState.changeFailed = true;
        }
        // Add current passcode error to the list
        if (!errors.includes('currentPasscode')) {
          errors.push('currentPasscode');
        }
      }
    }

    // Set all state changes at once
    setPasscodeState(newState);

    // Set error summary with all errors
    setErrorSummary(errors);

    // Show all errors at once
    setTimeout(() => {
      setIsAfterSubmit(true);
    }, 0);
  };

  const getFieldError = useCallback(
    (fieldName: keyof PasscodeState): { hasError: boolean; message: string | React.ReactNode } => {
      // Check for required field validation first
      if (
        fieldName === 'currentPasscode' &&
        passcodeState.currentPasscodeRequired &&
        !passcodeState.currentPasscode.trim()
      ) {
        return {
          hasError: true,
          message: 'Current passcode is required',
        };
      }

      if (
        fieldName === 'newPasscode' &&
        passcodeState.newPasscodeRequired &&
        !passcodeState.newPasscode.trim()
      ) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePasscode.rules.newPasscode'),
        };
      }

      if (
        fieldName === 'confirmNewPasscode' &&
        passcodeState.confirmNewPasscodeRequired &&
        !passcodeState.confirmNewPasscode.trim()
      ) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePasscode.rules.confirmNewPasscode'),
        };
      }

      // Then check for other validation errors
      if (fieldName === 'currentPasscode' && passcodeState.changeFailed) {
        return {
          hasError: true,
          message: t('S066'),
        };
      }

      if (
        fieldName === 'newPasscode' &&
        passcodeState.newPasscode.length > 0 &&
        !passcodeState.validPasscode
      ) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePasscode.rules.newPasscode'),
        };
      }

      if (
        fieldName === 'confirmNewPasscode' &&
        passcodeState.confirmNewPasscode.length > 0 &&
        !passcodeState.passcodesMatch
      ) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePasscode.rules.confirmNewPasscode'),
        };
      }

      return { hasError: false, message: '' };
    },
    [passcodeState, t]
  );

  const handleFieldBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const fieldName = name as keyof PasscodeState;
      const { hasError, message } = getFieldError(fieldName);

      if (hasError && message) {
        announceError(typeof message === 'string' ? message : 'Validation error');
      } else {
        clearAnnouncement();
      }
    },
    [getFieldError, announceError, clearAnnouncement]
  );

  const passcodeFields: PasscodeFieldConfig[] = [
    {
      name: 'currentPasscode',
      descriptionKey: 'S039',
      showPasscode: showCurrentPasscode,
      toggleFunction: toggleShowCurrentPasscode,
      announcement: announcementCurrentPasscode,
    },
    {
      name: 'newPasscode',
      descriptionKey: 'S040',
      showPasscode: showNewPasscode,
      toggleFunction: toggleShowNewPasscode,
      rulesDescription: t('S038'),
      announcement: announcementNewPasscode,
    },
    {
      name: 'confirmNewPasscode',
      descriptionKey: 'S041',
      showPasscode: showConfirmPasscode,
      toggleFunction: toggleShowConfirmPasscode,
      announcement: announcementConfirmPasscode,
    },
  ];

  return {
    passcodeState,
    showGreenCheck,
    isAfterSubmit,
    errorSummary,
    showCurrentPasscode,
    showNewPasscode,
    showConfirmPasscode,
    announcementCurrentPasscode,
    announcementNewPasscode,
    announcementConfirmPasscode,
    handlePasscodeChange,
    handleChangePasscode,
    handleFieldBlur,
    getFieldError,
    passcodeFields,
    announcement,
  };
};
